/**
 * Widget Monitoring System — admin panel section.
 *
 * Tabs:
 *  Corrupted Widgets — paginated global scan, severity-filtered, bulk/individual repair
 *  User Explorer     — search by email or UID, view ALL widget types, deep repair
 *  Audit Timeline    — collection-group query across all users' widget_audit logs
 *
 * Repair is genuinely destructive (writes normalised data back to Firestore) and
 * immediately re-fetches the document to confirm the write, showing a before/after diff.
 *
 * Soft Delete moves widgets to users/{docId}/deletedWidgets/{widgetId} so they
 * can be recovered. Nothing is hard-deleted from the primary document without a
 * logged soft-delete step first.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View, Text, TextInput, Pressable, ScrollView,
  ActivityIndicator, StyleSheet, Modal,
} from 'react-native';
import {
  getFirestore, collection, collectionGroup, query, where, getDocs,
  doc, updateDoc, setDoc, orderBy, limit, startAfter, serverTimestamp, getDoc,
} from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { validateWidget, repairWidget, WIDGET_SCHEMAS, isCardEmpty, isTrulyEmpty } from '../../services/WidgetWriteGuard';
import { logWidgetEvent } from '../../services/WidgetAuditLogger';

const isDevelopment = process.env.NODE_ENV === 'development';

// ─── constants ───────────────────────────────────────────────────────────────

const SEVERITY_ORDER = { critical: 0, medium: 1, low: 2, ok: 3 };
const SEVERITY_COLOR = { critical: '#ef4444', medium: '#f59e0b', low: '#3b82f6', ok: '#22c55e' };
const SEVERITY_BG    = { critical: '#fee2e2', medium: '#fef3c7', low: '#dbeafe', ok: '#dcfce7' };
const SEVERITY_LABEL = { critical: 'NEEDS ATTENTION', medium: 'NOT OPTIMIZED', low: 'NOT OPTIMIZED', ok: 'OK' };

const SCAN_BATCH = 75;   // users per Firestore batch
const SCAN_LIMIT = 600;  // max users scanned per session

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return '—';
  if (ts.toDate) return ts.toDate().toLocaleString();
  if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
  return String(ts);
}

function removeUndefined(obj) {
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = removeUndefined(v);
    }
    return out;
  }
  return obj;
}

/** Return a list of field-level diffs between two objects. */
function computeDiff(before, after) {
  if (!before && !after) return [];
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const diffs = [];
  for (const k of keys) {
    const bv = JSON.stringify((before || {})[k] ?? null);
    const av = JSON.stringify((after || {})[k] ?? null);
    if (bv !== av) diffs.push({ key: k, before: bv, after: av });
  }
  return diffs;
}

function getWidgetLabel(card) {
  const schema = WIDGET_SCHEMAS[card?.type];
  return schema ? schema.getLabel(card) : `(${card?.type || 'unknown'})`;
}

/** Human-readable widget type name */
function widgetTypeName(type) {
  return { Event: 'Event', TextBox: 'Text Box', ImageGallery: 'Photo Gallery',
    PictureFrame: 'Picture Frame', Countdown: 'Countdown', Poll: 'Poll',
    URLPreview: 'Link Preview', RSVP: 'RSVP' }[type] || type || 'Unknown Widget';
}

/** Plain-English translation of a raw validation error string */
function plainError(msg) {
  return msg
    .replace('eventTitle is empty', 'Missing event title')
    .replace('eventDate is empty', 'Missing event date')
    .replace('eventDescription is empty', 'Missing event description')
    .replace('targetDate is empty', 'Missing countdown date')
    .replace('question is empty', 'Missing poll question')
    .replace('url is empty', 'Missing link URL')
    .replace('No images added yet — widget will be blank for visitors', 'No photos added — will show blank to visitors')
    .replace('Widget type is empty — widget will not render on the page', 'Widget has no type — invisible to visitors')
    .replace('widget will not render', 'will not show on the page')
    .replace('structural arrays', 'data structure');
}

/**
 * Plain-English severity label + colour.
 * `awaiting` = admin-fixed but graduate still needs to recreate (needsReview still true).
 */
function severityDisplay(sev, fixed, awaiting) {
  if (awaiting) return { label: 'Awaiting graduate action', color: '#7c3aed', bg: '#ede9fe' };
  if (fixed)    return { label: 'Fixed',                   color: '#16a34a', bg: '#dcfce7' };
  return {
    critical: { label: 'Needs attention', color: '#dc2626', bg: '#fee2e2' },
    medium:   { label: 'Not optimized',   color: '#d97706', bg: '#fef3c7' },
    low:      { label: 'Not optimized',   color: '#2563eb', bg: '#dbeafe' },
    ok:       { label: 'Looks good',      color: '#16a34a', bg: '#dcfce7' },
  }[sev] || { label: sev, color: '#6b7280', bg: '#f3f4f6' };
}

/** Generate initials from a name string */
function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Derive display name from a scan result */
function displayName(result) {
  const first = result.firstName || '';
  const last  = result.lastName  || '';
  if (first || last) return `${first} ${last}`.trim();
  return result.email || result.uid || 'Unknown User';
}

/** Group a flat widget list by user (docId) */
function groupByUser(results) {
  const map = new Map();
  for (const r of results) {
    const key = r.docId || r.uid || r.email;
    if (!map.has(key)) {
      map.set(key, { key, docId: r.docId, uid: r.uid, uic: r.uic || null,
        pagesEvents: r.pagesEvents || null, email: r.email,
        firstName: r.firstName || '', lastName: r.lastName || '',
        profilePictureURL: r.profilePictureURL || null, widgets: [] });
    }
    map.get(key).widgets.push(r);
  }
  return Array.from(map.values());
}

/** Build the events page URL exactly like the admin user table does:
 *  baseUrl = user.pages.events  (full URL from Firestore)
 *  then append ?uic=... if present
 */
function eventsPageUrl(pagesEvents, uic) {
  if (!pagesEvents) return null;
  return uic ? `${pagesEvents}?uic=${uic}` : pagesEvents;
}

// ─── component ───────────────────────────────────────────────────────────────

export default function EventsWidgetMonitor({ adminId, theme }) {
  const db = getFirestore();

  const [subTab, setSubTab] = useState('explorer');

  // ── Explorer state ──────────────────────────────────────────────────────
  const [explorerQuery, setExplorerQuery]   = useState('');
  const [userData, setUserData]             = useState(null);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [cardAuditLogs, setCardAuditLogs]   = useState({});   // widgetId → logs[]
  const [auditExpanded, setAuditExpanded]   = useState(null);

  // ── Scanner state ───────────────────────────────────────────────────────
  const [scanResults, setScanResults]       = useState([]);
  const [scanRunning, setScanRunning]       = useState(false);
  const [scanProgress, setScanProgress]     = useState({ scanned: 0, corrupt: 0, done: false });
  const [scanSeverityFilter, setScanSeverityFilter] = useState('all');
  const [scanTypeFilter, setScanTypeFilter] = useState('all');
  const [scanSearchQuery, setScanSearchQuery] = useState('');
  const [scanLoadedFromCache, setScanLoadedFromCache] = useState(false);
  const scanCursorRef = useRef(null);
  const scanAbortRef  = useRef(false);
  // When Open Explorer is pressed, this ref signals searchUser to fire after
  // explorerQuery state has been committed.
  const pendingExplorerSearchRef = useRef(false);

  // ── Audit timeline state ────────────────────────────────────────────────
  const [auditTimeline, setAuditTimeline]   = useState([]);
  const [auditLoading, setAuditLoading]     = useState(false);
  const [auditFilter, setAuditFilter]       = useState('all');
  const [auditCursor, setAuditCursor]       = useState(null);
  const [auditUidFilter, setAuditUidFilter]           = useState('');
  const [auditWidgetIdFilter, setAuditWidgetIdFilter] = useState('');
  const [auditSourceFilter, setAuditSourceFilter]     = useState('all');

  // ── Diff modal state ────────────────────────────────────────────────────
  const [diffModal, setDiffModal] = useState(null); // { before, after, label }

  // ── Shared ──────────────────────────────────────────────────────────────
  const [actionLoading, setActionLoading]   = useState(null);
  const [statusMsg, setStatusMsg]           = useState(null);

  const t = theme || {};
  const c = {
    bg:          t.bgCard    || '#fff',
    bgSection:   t.bgSection || '#f8fafc',
    border:      t.border    || '#e2e8f0',
    textPrimary: t.textPrimary  || '#1f2937',
    textSecondary:t.textSecondary || '#4b5563',
    textMuted:   t.textMuted    || '#6b7280',
    primary:     t.primary   || '#4c956c',
    danger:      t.danger    || '#ef4444',
    warning:     t.warning   || '#f59e0b',
    success:     t.success   || '#22c55e',
    inputBg:     t.bgInput   || '#f9fafb',
  };

  const showStatus = useCallback((type, text) => {
    setStatusMsg({ type, text });
    setTimeout(() => setStatusMsg(null), 4000);
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // USER EXPLORER
  // ════════════════════════════════════════════════════════════════════════════

  const searchUser = useCallback(async () => {
    const q = explorerQuery.trim().toLowerCase();
    if (!q) return;
    setExplorerLoading(true);
    setUserData(null);
    setCardAuditLogs({});
    setSelectedCardId(null);

    try {
      let snap = null;
      const emailSnap = await getDocs(query(collection(db, 'users'), where('email', '==', q)));
      if (!emailSnap.empty) {
        snap = emailSnap.docs[0];
      } else {
        const uidSnap = await getDocs(query(collection(db, 'users'), where('uid', '==', q)));
        if (!uidSnap.empty) snap = uidSnap.docs[0];
      }

      if (!snap) { showStatus('err', `No user found for "${q}"`); return; }

      const data = snap.data();
      const cards = data.pages?.eventsPageConfig?.cards || [];
      setUserData({
        docId: snap.id,
        email: data.email || snap.id,
        uid: data.uid || null,
        uic: data.uic || null,
        pagesEvents: data.pages?.events || null,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        cards: cards.map(card => ({ ...card, _validation: validateWidget(card) })),
      });
    } catch (err) {
      showStatus('err', `Search failed: ${err.message}`);
    } finally {
      setExplorerLoading(false);
    }
  }, [db, explorerQuery, showStatus]);

  const fetchCardAudit = useCallback(async (widgetId) => {
    if (!userData?.docId) return;
    if (auditExpanded === widgetId) { setAuditExpanded(null); return; }

    if (!cardAuditLogs[widgetId]) {
      try {
        const snap = await getDocs(query(
          collection(db, 'users', userData.docId, 'widget_audit'),
          where('widgetId', '==', widgetId),
          orderBy('timestamp', 'desc'),
          limit(25),
        ));
        setCardAuditLogs(prev => ({ ...prev, [widgetId]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
      } catch (err) {
        if (isDevelopment) console.warn('[WidgetMonitor] audit fetch failed:', err);
      }
    }
    setAuditExpanded(widgetId);
  }, [db, userData, cardAuditLogs, auditExpanded]);

  /** Reload user data from Firestore — called after every repair/delete to confirm write. */
  const reloadUserData = useCallback(async () => {
    if (!userData?.docId) return;
    const snap = await getDoc(doc(db, 'users', userData.docId));
    if (!snap.exists()) return;
    const data = snap.data();
    const cards = data.pages?.eventsPageConfig?.cards || [];
    setUserData(prev => ({
      ...prev,
      cards: cards.map(card => ({ ...card, _validation: validateWidget(card) })),
    }));
  }, [db, userData]);

  // ─── Fix (merged Repair + Rebuild) ─────────────────────────────────────

  /**
   * Fix — single deterministic action replacing separate Repair and Rebuild.
   *
   * Strategy (evaluated in order):
   *   already ok + no review flag  → show clean status, no Firestore write
   *   structural repair resolves all issues → write, clear needsReview/status
   *   still broken after repair + has meaningful content → repair + flag needsReview
   *   still broken after repair + truly empty → reset to schema defaults + archive original
   *
   * Always produces a visible Firestore write whenever there is anything to fix.
   */
  const fixCard = useCallback(async (card) => {
    if (!userData) return;
    setActionLoading(card.id);
    try {
      const vBefore = validateWidget(card);

      // Widget is already valid and not flagged — nothing to do.
      if (vBefore.severity === 'ok' && !card.needsReview) {
        showStatus('ok', `Widget "${card.id}" is already clean — no fixes needed.`);
        setActionLoading(null);
        return;
      }

      const { _validation, ...cardData } = card;

      // Unknown/typeless widget — nothing to repair. Archive and remove it.
      const isKnownType = card.type && WIDGET_SCHEMAS[card.type];
      if (!isKnownType) {
        const widgetId = card.id || `unknown_${Date.now()}`;
        await setDoc(
          doc(db, 'users', userData.docId, 'deletedWidgets', widgetId + '_fix_' + Date.now()),
          { ...removeUndefined(cardData), _archivedAt: serverTimestamp(), _archivedBy: adminId || 'unknown', _reason: 'admin_fix_unknown_type' }
        );
        const userDocRef = doc(db, 'users', userData.docId);
        const freshSnap = await getDoc(userDocRef);
        const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
        const newCards = removeUndefined(freshCards.filter(c => c.id !== card.id).map((c, i) => ({ ...c, position: i })));
        await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });
        await logWidgetEvent(userData.docId, card.id || 'unknown', card.type || '(none)', 'widget_deleted', {
          source: 'admin', adminId: adminId || 'unknown',
          uid: userData.uid || null,
          before: cardData,
          caller: 'WidgetMonitor.fixCard.unknownType',
        });
        await reloadUserData();
        showStatus('ok', `Deleted unknown-type widget “${card.id || '(no id)'}” — empty and unrecognised, nothing to repair.`);
        setActionLoading(null);
        return;
      }

      const { repaired, changes } = repairWidget(card);
      const vAfter = validateWidget(repaired);
      const trulyEmpty = isTrulyEmpty(cardData);

      let fixedCard;
      let fixStrategy; // 'normalized' | 'flagged' | 'reset'

      // Stamp who fixed it and when (ISO string — serverTimestamp() is not allowed inside array elements)
      const fixMeta = {
        fixApplied: true,
        fixAppliedAt: new Date().toISOString(),
        fixAppliedBy: adminId || 'unknown',
      };

      if (vAfter.severity === 'ok') {
        // Structural repair resolved all issues — clear flags
        fixedCard = removeUndefined({ ...repaired, ...fixMeta, status: 'fixed', needsReview: false, ownerNotified: false });
        fixStrategy = 'normalized';
      } else if (!trulyEmpty) {
        // Meaningful content present but issues remain — repair + flag for owner
        fixedCard = removeUndefined({ ...repaired, ...fixMeta, status: 'fixed', needsReview: true, ownerNotified: true });
        fixStrategy = 'flagged';
      } else {
        // Truly empty + still broken — reset to schema defaults
        const schema = WIDGET_SCHEMAS[card.type] || {};
        fixedCard = removeUndefined({
          ...(schema.defaults || {}),
          id: card.id,
          position: card.position ?? 0,
          type: card.type,
          ...fixMeta,
          status: 'fixed',
          needsReview: true,
          ownerNotified: true,
        });
        fixStrategy = 'reset';
        // Archive the empty original before overwriting
        await setDoc(
          doc(db, 'users', userData.docId, 'deletedWidgets', card.id + '_fix_' + Date.now()),
          { ...removeUndefined(cardData), _archivedAt: serverTimestamp(), _archivedBy: adminId || 'unknown', _reason: 'admin_fix_reset' }
        );
      }

      const userDocRef = doc(db, 'users', userData.docId);
      const freshSnap = await getDoc(userDocRef);
      const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
      const newCards = removeUndefined(freshCards.map(c => (c.id === card.id ? fixedCard : c)));
      await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });

      await logWidgetEvent(userData.docId, card.id, card.type, 'widget_fixed', {
        source: 'admin', adminId: adminId || 'unknown',
        uid: userData.uid || null,
        before: cardData, after: fixedCard,
        caller: 'WidgetMonitor.fixCard',
      });

      await reloadUserData();

      const diffChanges = [
        ...changes,
        ...(fixStrategy === 'flagged'    ? ['Required content still missing — flagged for owner review'] : []),
        ...(fixStrategy === 'reset'      ? ['Widget was truly empty — reset to clean schema defaults', 'Original archived to deletedWidgets', 'Flagged for owner review'] : []),
        ...(fixStrategy === 'normalized' && changes.length === 0 ? ['Review flag cleared — widget content is valid'] : []),
      ];
      setDiffModal({ label: `Fix: ${card.id}`, before: cardData, after: fixedCard, changes: diffChanges });

      const msgs = {
        normalized: `Fixed "${card.id}". ${changes.length ? `${changes.length} structural issue(s) corrected.` : 'Review flag cleared — content is valid.'}`,
        flagged:    `Fixed "${card.id}". Structure normalized. Flagged for owner review (required content still needed).`,
        reset:      `Fixed "${card.id}". Widget was empty — reset to clean schema. Original archived. Owner will see a review notice.`,
      };
      showStatus('ok', msgs[fixStrategy]);
    } catch (err) {
      showStatus('err', `Fix failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [db, userData, adminId, reloadUserData, showStatus]);

  /** Soft delete — moves widget to deletedWidgets subcollection, removes from cards[]. */
  const softDeleteCard = useCallback(async (card) => {
    if (!userData) return;
    if (!window.confirm(
      `Soft-delete widget "${card.id}"?\n\nIt will be moved to the deletedWidgets collection and removed from the user's page. This can be recovered from Firestore console.`
    )) return;

    setActionLoading(card.id);
    try {
      const { _validation, ...cardData } = card;

      // Safety check: only truly empty cards can be deleted.
      // Cards with partial data (photos, RSVPs, coords, etc.) get flagged as incomplete instead.
      if (!isTrulyEmpty(cardData)) {
        const userDocRef = doc(db, 'users', userData.docId);
        const freshSnap = await getDoc(userDocRef);
        const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
        const flagIdx = freshCards.findIndex(c => c.id === card.id);
        if (flagIdx !== -1) {
          freshCards[flagIdx] = { ...freshCards[flagIdx], status: 'incomplete', needsReview: true };
          await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': removeUndefined(freshCards) });
          await logWidgetEvent(userData.docId, card.id, card.type, 'widget_updated', {
            source: 'admin', adminId: adminId || 'unknown',
            uid: userData.uid || null,
            before: cardData, after: { ...cardData, status: 'incomplete', needsReview: true },
            caller: 'WidgetMonitor.softDeleteCard.flagIncomplete',
          });
        }
        await reloadUserData();
        showStatus('ok', `Widget "${card.id}" has partial data — flagged as Incomplete. No data was removed.`);
        return;
      }

      // Truly empty — safe to archive and remove.
      const userDocRef = doc(db, 'users', userData.docId);

      // Archive to soft-delete subcollection
      await setDoc(doc(db, 'users', userData.docId, 'deletedWidgets', card.id), {
        ...removeUndefined(cardData),
        _deletedAt: serverTimestamp(),
        _deletedBy: adminId || 'unknown',
        _reason: 'admin_soft_delete',
      });

      // Always re-fetch fresh Firestore data before writing to avoid stale React state no-ops.
      const freshSnap2 = await getDoc(userDocRef);
      const freshCards2 = freshSnap2.data()?.pages?.eventsPageConfig?.cards || [];
      const newCards = removeUndefined(
        freshCards2
          .filter(c => c.id !== card.id)
          .map((c, i) => ({ ...c, position: i }))
      );

      await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });

      await logWidgetEvent(userData.docId, card.id, card.type, 'widget_deleted', {
        source: 'admin', adminId: adminId || 'unknown',
        uid: userData.uid || null,
        before: cardData, caller: 'WidgetMonitor.softDeleteCard',
      });

      await reloadUserData();
      showStatus('ok', `Widget "${card.id}" soft-deleted and archived.`);
    } catch (err) {
      showStatus('err', `Soft delete failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [db, userData, adminId, reloadUserData, showStatus]);

  /** Repair all empty/critical cards for a user at once. */
  const repairAllEmpty = useCallback(async () => {
    if (!userData) return;
    setActionLoading('__all__');
    try {
      // Always re-fetch fresh Firestore data before any write.
      const userDocRef = doc(db, 'users', userData.docId);
      const freshSnap = await getDoc(userDocRef);
      const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];

      const toDelete = []; // truly empty → safe to archive + remove
      const toFlag   = []; // critical but has some meaningful data → flag incomplete
      const toKeep   = []; // not critical → preserve as-is

      for (const c of freshCards) {
        if (!isCardEmpty(c)) {
          toKeep.push(c);
        } else if (isTrulyEmpty(c)) {
          toDelete.push(c);
        } else {
          // Has critical severity (missing required text) but still holds meaningful data
          toFlag.push({ ...c, status: 'incomplete', needsReview: true });
        }
      }

      // Soft-archive each truly-empty card
      for (const card of toDelete) {
        await setDoc(doc(db, 'users', userData.docId, 'deletedWidgets', card.id + '_bulk_' + Date.now()), {
          ...removeUndefined(card),
          _deletedAt: serverTimestamp(),
          _deletedBy: adminId || 'unknown',
          _reason: 'admin_bulk_repair',
        });
        await logWidgetEvent(userData.docId, card.id, card.type, 'widget_deleted', {
          source: 'admin', adminId: adminId || 'unknown',
          uid: userData.uid || null,
          before: card,
          caller: 'WidgetMonitor.repairAllEmpty',
        });
      }

      // Log flag operations for incomplete cards
      for (const flagged of toFlag) {
        const orig = freshCards.find(c => c.id === flagged.id);
        if (orig) {
          await logWidgetEvent(userData.docId, flagged.id, flagged.type, 'widget_updated', {
            source: 'admin', adminId: adminId || 'unknown',
            uid: userData.uid || null,
            before: orig, after: flagged,
            caller: 'WidgetMonitor.repairAllEmpty.flagIncomplete',
          });
        }
      }

      const newCards = removeUndefined(
        [...toKeep, ...toFlag].map((c, i) => ({ ...c, position: i }))
      );
      await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });
      await reloadUserData();

      const parts = [];
      if (toDelete.length) parts.push(`${toDelete.length} empty widget(s) archived`);
      if (toFlag.length) parts.push(`${toFlag.length} incomplete widget(s) flagged for review`);
      showStatus('ok', parts.length ? parts.join(', ') + '. Cards re-indexed.' : 'No changes needed.');
    } catch (err) {
      showStatus('err', `Bulk repair failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [db, userData, adminId, reloadUserData, showStatus]);

  // ════════════════════════════════════════════════════════════════════════════
  // GLOBAL SCANNER
  // ════════════════════════════════════════════════════════════════════════════

  const runScan = useCallback(async (resume = false) => {
    if (scanRunning) return;
    if (!resume) {
      setScanResults([]);
      setScanProgress({ scanned: 0, corrupt: 0, done: false });
      scanCursorRef.current = null;
      setScanLoadedFromCache(false);
      try { localStorage.removeItem('mtWidgetMonitor_scanResults'); localStorage.removeItem('mtWidgetMonitor_scanProgress'); } catch (_) {}
    }
    setScanRunning(true);
    scanAbortRef.current = false;

    let scanned = resume ? scanProgress.scanned : 0;
    let corrupt = resume ? scanProgress.corrupt : 0;

    try {
      while (scanned < SCAN_LIMIT && !scanAbortRef.current) {
        let q = query(collection(db, 'users'), orderBy('__name__'), limit(SCAN_BATCH));
        if (scanCursorRef.current) q = query(q, startAfter(scanCursorRef.current));

        const snap = await getDocs(q);
        if (snap.empty) break;

        scanCursorRef.current = snap.docs[snap.docs.length - 1];

        const batch = [];
        for (const docSnap of snap.docs) {
          const data = docSnap.data();
          scanned++;
          if (data.isTester) continue;
          const cards = data.pages?.eventsPageConfig?.cards;
          if (!Array.isArray(cards) || cards.length === 0) continue;

          for (const card of cards) {
            const v = validateWidget(card);
            if (v.severity === 'ok') continue;
            batch.push({
              docId:             docSnap.id,
              uid:               data.uid || null,
              uic:               data.uic || null,
              pagesEvents:       data.pages?.events || null,
              email:             data.email || docSnap.id,
              firstName:         data.firstName || '',
              lastName:          data.lastName  || '',
              profilePictureURL: data.profilePictureURL || null,
              widgetId:          card.id   || '(no id)',
              widgetType:        card.type || '(no type)',
              label:             getWidgetLabel(card),
              severity:          v.severity,
              errors:            v.errors,
              fixApplied:        card.fixApplied    || false,
              fixAppliedBy:      card.fixAppliedBy  || null,
              fixAppliedAt:      card.fixAppliedAt  || null,
              needsReview:       card.needsReview   || false,
              ownerNotified:     card.ownerNotified || false,
              card,
            });
            corrupt++;
          }
        }

        if (batch.length > 0) setScanResults(prev => [...prev, ...batch]);
        setScanProgress({ scanned, corrupt, done: false });

        if (snap.docs.length < SCAN_BATCH) break;
      }
    } catch (err) {
      showStatus('err', `Scan error: ${err.message}`);
    } finally {
      setScanRunning(false);
      setScanProgress(prev => ({ ...prev, done: true }));
    }
  }, [db, scanRunning, scanProgress, showStatus]);

  const fixScanResult = useCallback(async (result) => {
    setActionLoading(result.widgetId);
    try {
      // Unknown/typeless widget — nothing to repair. Archive and remove it.
      const isKnownType = result.card.type && WIDGET_SCHEMAS[result.card.type];
      if (!isKnownType) {
        await setDoc(
          doc(db, 'users', result.docId, 'deletedWidgets', result.widgetId + '_fix_' + Date.now()),
          { ...removeUndefined(result.card), _archivedAt: serverTimestamp(), _archivedBy: adminId || 'unknown', _reason: 'admin_fix_unknown_type' }
        );
        const userDocRef = doc(db, 'users', result.docId);
        const freshSnap = await getDoc(userDocRef);
        if (!freshSnap.exists()) { showStatus('err', 'User doc not found.'); return; }
        const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
        const newCards = removeUndefined(freshCards.filter(c => c.id !== result.widgetId).map((c, i) => ({ ...c, position: i })));
        await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });
        await logWidgetEvent(result.docId, result.widgetId, result.widgetType, 'widget_deleted', {
          source: 'admin', adminId: adminId || 'unknown',
          uid: result.uid || null,
          before: result.card,
          caller: 'WidgetMonitor.fixScanResult.unknownType',
        });
        setScanResults(prev => prev.filter(r => !(r.widgetId === result.widgetId && r.docId === result.docId)));
        showStatus('ok', `Deleted unknown-type widget “${result.widgetId}” — empty and unrecognised, nothing to repair.`);
        setActionLoading(null);
        return;
      }

      const { repaired, changes } = repairWidget(result.card);
      const vAfter = validateWidget(repaired);

      // Stamp who fixed it and when (ISO string — serverTimestamp() not allowed inside array elements)
      const fixMeta = {
        fixApplied: true,
        fixAppliedAt: new Date().toISOString(),
        fixAppliedBy: adminId || 'unknown',
      };

      // Apply the same three-strategy logic as fixCard
      let fixedCard;
      let ownerWasNotified = false;
      if (vAfter.severity === 'ok' || vAfter.severity === 'low') {
        fixedCard = removeUndefined({ ...repaired, ...fixMeta, status: 'fixed', needsReview: false, ownerNotified: false });
      } else if (!isTrulyEmpty(result.card)) {
        ownerWasNotified = true;
        fixedCard = removeUndefined({ ...repaired, ...fixMeta, status: 'fixed', needsReview: true, ownerNotified: true });
      } else {
        ownerWasNotified = true;
        const schema = WIDGET_SCHEMAS[result.card.type] || {};
        fixedCard = removeUndefined({
          ...(schema.defaults || {}),
          id: result.card.id || result.widgetId,
          position: result.card.position ?? 0,
          type: result.card.type,
          ...fixMeta,
          status: 'fixed',
          needsReview: true,
          ownerNotified: true,
        });
        // Archive truly-empty original before overwriting
        await setDoc(
          doc(db, 'users', result.docId, 'deletedWidgets', result.widgetId + '_fix_' + Date.now()),
          { ...removeUndefined(result.card), _archivedAt: serverTimestamp(), _archivedBy: adminId || 'unknown', _reason: 'admin_fix_reset' }
        );
      }

      const userDocRef = doc(db, 'users', result.docId);
      const freshSnap  = await getDoc(userDocRef);
      if (!freshSnap.exists()) { showStatus('err', 'User doc not found.'); return; }
      const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
      const newCards   = removeUndefined(freshCards.map(c => (c.id === result.widgetId ? fixedCard : c)));

      await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });
      await logWidgetEvent(result.docId, result.widgetId, result.widgetType, 'widget_fixed', {
        source: 'admin', adminId: adminId || 'unknown',
        uid: result.uid || null,
        before: result.card, after: fixedCard,
        caller: 'WidgetMonitor.fixScanResult',
      });

      const newV = validateWidget(fixedCard);
      setScanResults(prev => prev.map(r =>
        r.widgetId === result.widgetId && r.docId === result.docId
          ? {
              ...r,
              severity:       newV.severity,
              errors:         newV.errors,
              card:           fixedCard,
              fixApplied:     true,
              _fixApplied:    true,
              _fixAppliedBy:  adminId || 'unknown',
              _ownerNotified: ownerWasNotified,
            }
          : r
      ));
      setDiffModal({ label: `Fix: ${result.widgetId}`, before: result.card, after: fixedCard, changes });
      showStatus('ok', `Fixed "${result.widgetId}". ${changes.length} structural issue(s) corrected.${ownerWasNotified ? ' Owner notified via review banner.' : ''}`);
    } catch (err) {
      showStatus('err', `Fix failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [db, adminId, showStatus]);

  // ─── Bulk-fix all low-severity (structural array) widgets ────────────────
  const [bulkFixRunning, setBulkFixRunning] = useState(false);

  const fixAllLow = useCallback(async () => {
    // Fix low-severity widgets AND repair structural array issues on medium widgets
    const targets = scanResults.filter(r => {
      if ((r._fixApplied || r.fixApplied) || r.needsReview) return false;
      if (r.severity === 'low') return true;
      // Medium widgets that have at least one structural array error alongside text errors
      if (r.severity === 'medium') return r.errors.some(e => e.includes('not an array'));
      return false;
    });
    if (targets.length === 0) { showStatus('ok', 'No minor issues left to fix.'); return; }
    setBulkFixRunning(true);
    let fixed = 0, failed = 0;
    for (const result of targets) {
      try {
        const { repaired, changes } = repairWidget(result.card);
        const fixMeta = { fixApplied: true, fixAppliedAt: new Date().toISOString(), fixAppliedBy: adminId || 'unknown' };
        const fixedCard = removeUndefined({ ...repaired, ...fixMeta });
        const userDocRef = doc(db, 'users', result.docId);
        const freshSnap  = await getDoc(userDocRef);
        if (!freshSnap.exists()) { failed++; continue; }
        const freshCards = freshSnap.data()?.pages?.eventsPageConfig?.cards || [];
        const newCards   = removeUndefined(freshCards.map(c => (c.id === result.widgetId ? fixedCard : c)));
        await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': newCards });
        await logWidgetEvent(result.docId, result.widgetId, result.widgetType, 'widget_fixed', {
          source: 'admin', adminId: adminId || 'unknown', uid: result.uid || null,
          before: result.card, after: fixedCard, caller: 'WidgetMonitor.fixAllLow',
        });
        const newV = validateWidget(fixedCard);
        setScanResults(prev => prev.map(r =>
          r.widgetId === result.widgetId && r.docId === result.docId
            ? { ...r, severity: newV.severity, errors: newV.errors, card: fixedCard, fixApplied: true, _fixApplied: true, _fixAppliedBy: adminId || 'unknown' }
            : r
        ));
        fixed++;
      } catch (_) { failed++; }
    }
    setBulkFixRunning(false);
    showStatus('ok', `Fixed ${fixed} minor issue${fixed !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`);
  }, [db, adminId, scanResults, showStatus]);

  // ════════════════════════════════════════════════════════════════════════════
  // AUDIT TIMELINE (collection group)
  // ════════════════════════════════════════════════════════════════════════════

  const loadAuditTimeline = useCallback(async (more = false) => {
    setAuditLoading(true);
    try {
      let q = query(
        collectionGroup(db, 'widget_audit'),
        orderBy('timestamp', 'desc'),
        limit(50),
      );
      if (auditFilter !== 'all') q = query(q, where('changeType', '==', auditFilter));
      if (more && auditCursor) q = query(q, startAfter(auditCursor));

      const snap = await getDocs(q);
      const logs = snap.docs.map(d => ({ id: d.id, _path: d.ref.path, ...d.data() }));

      setAuditTimeline(prev => more ? [...prev, ...logs] : logs);
      setAuditCursor(snap.docs[snap.docs.length - 1] || null);
    } catch (err) {
      showStatus('err', `Audit load failed: ${err.message}`);
    } finally {
      setAuditLoading(false);
    }
  }, [db, auditFilter, auditCursor, showStatus]);

  // ── Persist / restore scan results across page refreshes ──────────────
  // Load from localStorage once on mount.
  useEffect(() => {
    try {
      const saved         = localStorage.getItem('mtWidgetMonitor_scanResults');
      const savedProgress = localStorage.getItem('mtWidgetMonitor_scanProgress');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setScanResults(parsed);
          setScanLoadedFromCache(true);
        }
      }
      if (savedProgress) setScanProgress(JSON.parse(savedProgress));
    } catch (_) {}
  }, []);

  // Persist scan results whenever they change.
  useEffect(() => {
    if (scanResults.length > 0) {
      try { localStorage.setItem('mtWidgetMonitor_scanResults', JSON.stringify(scanResults)); } catch (_) {}
    }
  }, [scanResults]);

  useEffect(() => {
    if (scanProgress.scanned > 0) {
      try { localStorage.setItem('mtWidgetMonitor_scanProgress', JSON.stringify(scanProgress)); } catch (_) {}
    }
  }, [scanProgress]);

  // Auto-load audit timeline the first time the Audit tab is opened.
  const auditBootedRef = useRef(false);
  useEffect(() => {
    if (subTab === 'audit' && !auditBootedRef.current) {
      auditBootedRef.current = true;
      loadAuditTimeline(false);
    }
  }, [subTab, loadAuditTimeline]);

  // When the Explorer tab is switched to via Open Explorer button, auto-fire searchUser.
  useEffect(() => {
    if (subTab === 'explorer' && pendingExplorerSearchRef.current && explorerQuery) {
      pendingExplorerSearchRef.current = false;
      searchUser();
    }
  }, [subTab, explorerQuery, searchUser]);

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ════════════════════════════════════════════════════════════════════════════

  // Only CRITICAL widgets (all required text fields blank) are eligible for bulk archive.
  // MEDIUM and LOW are intentionally excluded — they still render and don't need forced admin action.
  const criticalCount = userData?.cards.filter(c => c._validation?.severity === 'critical').length ?? 0;
  // Keep a display count for the user-header badge (shows any non-ok widget)
  const corruptCount  = userData?.cards.filter(c => c._validation?.severity !== 'ok').length ?? 0;

  const filteredScanResults = (() => {
    // Truly done = admin fixed AND graduate has no remaining action
    const isFullyFixed   = r => !!(r._fixApplied || r.fixApplied) && !r.needsReview;
    // Awaiting = admin archived/fixed it but graduate still needs to recreate the widget
    const isAwaiting     = r => !!(r._fixApplied || r.fixApplied) &&  !!r.needsReview;
    const isActiveIssue  = r => !isFullyFixed(r) && !isAwaiting(r);

    let results;
    if (scanSeverityFilter === 'fixed') {
      results = scanResults.filter(r => isFullyFixed(r));
    } else if (scanSeverityFilter === 'awaiting') {
      results = scanResults.filter(r => isAwaiting(r));
    } else {
      // 'all', 'critical', 'medium' — show active issues + awaiting (both need attention)
      results = scanResults.filter(r => isActiveIssue(r) || isAwaiting(r));
    }

    if (scanSeverityFilter === 'critical') {
      results = results.filter(r => r.severity === 'critical' && !isAwaiting(r));
    } else if (scanSeverityFilter === 'medium') {
      // 'Not optimized' covers both medium and low active issues
      results = results.filter(r => (r.severity === 'medium' || r.severity === 'low') && !isAwaiting(r));
    }
    // 'all', 'awaiting', 'fixed' need no further severity filtering

    if (scanTypeFilter !== 'all') {
      results = results.filter(r => r.widgetType === scanTypeFilter);
    }

    if (scanSearchQuery.trim()) {
      const needle = scanSearchQuery.trim().toLowerCase();
      results = results.filter(r =>
        r.email?.toLowerCase().includes(needle) ||
        (r.uid && r.uid.toLowerCase().includes(needle)) ||
        r.widgetId?.toLowerCase().includes(needle)
      );
    }

    return results.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  })();

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <View style={{ flex: 1 }}>


      {/* ── Sub-tabs ── */}
      <View style={[S.tabBar, { backgroundColor: c.bg, borderBottomColor: c.border }]}>
        {[
          { id: 'explorer',  label: 'User Explorer', icon: 'search-outline' },
          { id: 'corrupted', label: (() => {
              if (!scanResults.length) return 'Widget Health';
              const fullyFixed = r => !!(r._fixApplied || r.fixApplied) && !r.needsReview;
              const awaiting   = r => !!(r._fixApplied || r.fixApplied) &&  !!r.needsReview;
              const active     = scanResults.filter(r => !fullyFixed(r) && !awaiting(r));
              const awaitingN  = scanResults.filter(awaiting).length;
              const critical   = active.filter(r => r.severity === 'critical').length;
              const notOpt     = active.filter(r => r.severity !== 'critical').length;
              const parts = [];
              if (critical > 0) parts.push(`${critical} urgent`);
              if (notOpt > 0)   parts.push(`${notOpt} not optimized`);
              if (awaitingN > 0) parts.push(`${awaitingN} awaiting grad`);
              if (parts.length > 0) return `Widget Health (${parts.join(' · ')})`;
              return 'Widget Health (all clear)';
            })(), icon: 'alert-circle-outline' },
          { id: 'audit',     label: 'Widget Logs', icon: 'layers-outline' },
        ].map(tab => (
          <Pressable
            key={tab.id}
            style={[S.tabBtn, subTab === tab.id && { borderBottomColor: c.primary, borderBottomWidth: 2 }]}
            onPress={() => setSubTab(tab.id)}
          >
            <View style={S.tabBtnContent}>
              <Ionicons name={tab.icon} size={16} color={subTab === tab.id ? c.primary : c.textMuted} />
              <Text style={[S.tabBtnText, { color: subTab === tab.id ? c.primary : c.textMuted }]}> {tab.label}</Text>
            </View>
          </Pressable>
        ))}
      </View>

      {/* ── Status banner ── */}
      {statusMsg && (
        <View style={[S.statusBanner, { backgroundColor: statusMsg.type === 'ok' ? '#dcfce7' : '#fee2e2' }]}>
          <Text style={{ color: statusMsg.type === 'ok' ? '#166534' : '#991b1b', fontSize: 13, flex: 1 }}>
            {statusMsg.text}
          </Text>
          <Pressable onPress={() => setStatusMsg(null)}>
            <Text style={{ color: statusMsg.type === 'ok' ? '#166534' : '#991b1b', marginLeft: 12 }}>✕</Text>
          </Pressable>
        </View>
      )}

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>

        {/* ══════════════════════════════════════════════════════════════════════
            USER EXPLORER
        ══════════════════════════════════════════════════════════════════════ */}
        {subTab === 'explorer' && (
          <View>
            <View style={S.searchRow}>
              <TextInput
                style={[S.searchInput, { backgroundColor: c.inputBg, color: c.textPrimary, borderColor: c.border }]}
                placeholder="Enter user email or UID…"
                placeholderTextColor={c.textMuted}
                value={explorerQuery}
                onChangeText={setExplorerQuery}
                onSubmitEditing={searchUser}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <Pressable style={[S.primaryBtn, { backgroundColor: c.primary }]} onPress={searchUser} disabled={explorerLoading}>
                <Text style={S.primaryBtnText}>{explorerLoading ? 'Searching…' : 'Search'}</Text>
              </Pressable>
            </View>

            {explorerLoading && <ActivityIndicator style={{ marginTop: 30 }} color={c.primary} />}

            {userData && (
              <View>
                {/* User header */}
                <View style={[S.userCard, { backgroundColor: c.bg, borderColor: c.border }]}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View style={{ flex: 1 }}>
                      <Text style={[S.userName, { color: c.textPrimary }]}>
                        {userData.firstName} {userData.lastName}
                      </Text>
                      <Text style={[S.userMeta, { color: c.textMuted }]}>
                        {userData.email} · UID: {userData.uid || userData.docId}
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <SeverityBadge label={`${userData.cards.length} total`} bg="#dbeafe" fg="#1e40af" />
                        {corruptCount > 0 && <SeverityBadge label={`${corruptCount} issues`} bg="#fee2e2" fg="#991b1b" />}
                      </View>
                      {/* View as Owner — opens page exactly as the graduate sees it */}
                      {eventsPageUrl(userData.pagesEvents, userData.uic) && (
                        <Pressable
                          style={[S.viewPageBtn, { paddingHorizontal: 10, paddingVertical: 6 }]}
                          onPress={() => window.open(eventsPageUrl(userData.pagesEvents, userData.uic), '_blank')}
                        >
                          <Ionicons name="eye-outline" size={13} color="#fff" style={{ marginRight: 5 }} />
                          <Text style={[S.viewPageBtnText, { fontSize: 12 }]}>View as Owner</Text>
                        </Pressable>
                      )}
                      {criticalCount > 0 && (
                        <Pressable
                          style={[S.archiveBtn, { opacity: actionLoading === '__all__' ? 0.5 : 1 }]}
                          onPress={repairAllEmpty}
                          disabled={actionLoading === '__all__'}
                        >
                          <View style={S.actionBtnContent}>
                            <Ionicons name="archive-outline" size={13} color="#1e40af" />
                            <Text style={S.archiveBtnText}>
                              {actionLoading === '__all__' ? 'Working…' : `Archive Empty Widgets (${criticalCount})`}
                            </Text>
                          </View>
                        </Pressable>
                      )}
                    </View>
                  </View>
                </View>

                {userData.cards.length === 0 && (
                  <Text style={[S.emptyNote, { color: c.textMuted }]}>This user has no widgets.</Text>
                )}

                {/* Widget list */}
                {userData.cards.map((card, idx) => {
                  const v             = card._validation || validateWidget(card);
                  const isSelected    = selectedCardId === card.id;
                  const isActing      = actionLoading === card.id;
                  const auditShown    = auditExpanded === card.id;
                  const logs          = cardAuditLogs[card.id] || [];
                  const cardFixed     = card.fixApplied || false;
                  const cardFixedBy   = card.fixAppliedBy || null;
                  const cardFixedAt   = card.fixAppliedAt || null;
                  const cardOwnerNotified = card.ownerNotified || false;

                  return (
                    <View key={card.id || idx} style={[S.widgetRow, {
                      borderColor: cardFixed ? '#a3e635' : (SEVERITY_COLOR[v.severity] || c.border),
                    }]}>
                      {/* Row header */}
                      <Pressable
                        style={S.widgetRowHeader}
                        onPress={() => setSelectedCardId(prev => (prev === card.id ? null : card.id))}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={[S.widgetTitle, { color: c.textPrimary }]}>
                            [{idx}] <Text style={{ color: c.textMuted }}>{card.type}</Text> — {getWidgetLabel(card)}
                          </Text>
                          <Text style={[S.widgetMeta, { color: c.textMuted }]}>
                            id: {card.id || '—'} · pos: {card.position ?? idx}
                          </Text>
                        </View>
                        <View style={{ alignItems: 'flex-end', gap: 3 }}>
                          <Text style={[S.sevBadge, { color: cardFixed ? '#65a30d' : (SEVERITY_COLOR[v.severity] || c.textMuted) }]}>
                            {cardFixed ? 'FIXED' : (SEVERITY_LABEL[v.severity] || v.severity)}
                          </Text>
                          {cardFixed && cardFixedBy && (
                            <Text style={{ fontSize: 10, color: c.textMuted }}>
                              by {cardFixedBy}{cardFixedAt ? ` · ${new Date(cardFixedAt).toLocaleDateString()}` : ''}
                            </Text>
                          )}
                        </View>
                      </Pressable>
                      {/* Owner notified badge */}
                      {cardOwnerNotified && (
                        <View style={[S.ownerNotifiedBadge, { marginTop: 6 }]}>
                          <Ionicons name="notifications-outline" size={12} color="#166534" style={{ marginRight: 4 }} />
                          <Text style={S.ownerNotifiedText}>Owner notified via review banner</Text>
                        </View>
                      )}

                      {/* Validation errors */}
                      {v.errors?.length > 0 && (
                        <View style={{ marginTop: 4 }}>
                          {v.errors.map((e, i) => (
                            <Text key={i} style={[S.errorLine, { color: c.danger }]}>• {e}</Text>
                          ))}
                        </View>
                      )}

                      {/* Raw data panel */}
                      {isSelected && (
                        <View style={[S.rawPanel, { backgroundColor: c.inputBg, borderColor: c.border }]}>
                          <Text style={[S.rawLabel, { color: c.textMuted }]}>Raw Firestore Data:</Text>
                          <Text style={[S.rawText, { color: c.textPrimary }]}>
                            {JSON.stringify(
                              Object.fromEntries(Object.entries(card).filter(([k]) => k !== '_validation')),
                              null, 2
                            )}
                          </Text>
                        </View>
                      )}

                      {/* Action buttons — severity-gated
                           CRITICAL (not fixed) : Fix + Delete + View Page + Audit Log
                           CRITICAL (fixed)     : Already Fixed badge + Delete + View Page + Audit Log
                           MEDIUM               : View Page + Audit Log only (no Fix)
                           LOW                  : LOG ONLY badge — no interaction buttons
                      */}
                      {v.severity === 'low' ? (
                        <View style={[S.actionRow, { marginTop: 8 }]}>
                          <View style={S.logOnlyBadge}>
                            <Ionicons name="information-circle-outline" size={13} color="#92400e" style={{ marginRight: 4 }} />
                            <Text style={S.logOnlyText}>LOG ONLY — structural arrays auto-normalize on next user save</Text>
                          </View>
                        </View>
                      ) : (
                        <View style={S.actionRow}>
                          {v.severity === 'critical' && (
                            cardFixed ? (
                              <View style={S.alreadyFixedBadge}>
                                <Ionicons name="checkmark-circle-outline" size={13} color="#166534" style={{ marginRight: 4 }} />
                                <Text style={S.alreadyFixedText}>Already Fixed</Text>
                              </View>
                            ) : (
                              <ActionBtn iconName="construct-outline" label="Fix" bg="#dbeafe" fg="#1e40af" loading={isActing} onPress={() => fixCard(card)} />
                            )
                          )}
                          {v.severity === 'critical' && (
                            <ActionBtn iconName="trash-outline" label="Delete" bg="#fee2e2" fg="#991b1b" loading={isActing} onPress={() => softDeleteCard(card)} />
                          )}
                          {(v.severity === 'critical' || v.severity === 'medium') && (
                            <ActionBtn
                              iconName="open-outline"
                              label="View Page"
                              bg="#f1f5f9" fg="#475569"
                              loading={false}
                              onPress={() => window.open(eventsPageUrl(userData.pagesEvents, userData.uic), '_blank')}
                            />
                          )}
                          {v.severity === 'medium' && (
                            <View style={[S.inspectOnlyBadge]}>
                              <Ionicons name="eye-outline" size={13} color="#92400e" style={{ marginRight: 4 }} />
                              <Text style={S.inspectOnlyText}>INSPECT ONLY — no Fix (partial data, still functional)</Text>
                            </View>
                          )}
                          {/* Audit Log available for all non-ok, non-low severities */}
                          <ActionBtn
                            iconName="time-outline"
                            label={auditShown ? 'Hide Logs' : 'Audit Log'}
                            bg="#f3e8ff" fg="#6b21a8"
                            loading={false}
                            onPress={() => fetchCardAudit(card.id)}
                          />
                        </View>
                      )}

                      {/* Audit log */}
                      {auditShown && (
                        <View style={[S.auditSection, { borderTopColor: c.border }]}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                            <Ionicons name="time-outline" size={14} color="#6b21a8" style={{ marginRight: 6 }} />
                            <Text style={[S.auditTitle, { color: '#6b21a8', fontWeight: '700' }]}>
                              Audit Log
                              {logs.length > 0 ? ` (${logs.length} records)` : ''}
                            </Text>
                          </View>
                          {logs.length === 0 ? (
                            <Text style={[S.emptyNote, { color: c.textMuted }]}>No audit records found.</Text>
                          ) : logs.map(log => (
                            <AuditLogRow key={log.id} log={log} textPrimary={c.textPrimary} textMuted={c.textMuted} danger={c.danger} border={c.border} onDiff={setDiffModal} />
                          ))}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            CORRUPTED WIDGETS SCANNER
        ══════════════════════════════════════════════════════════════════════ */}
        {subTab === 'corrupted' && (
          <View>
            {/* Scan controls */}
            <View style={[S.scanHeader, { backgroundColor: c.bg, borderColor: c.border }]}>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <Pressable
                  style={[S.primaryBtn, { backgroundColor: scanRunning ? c.textMuted : c.primary, flexDirection: 'row', alignItems: 'center', gap: 6 }]}
                  onPress={() => scanRunning ? (scanAbortRef.current = true) : runScan(false)}
                >
                  <Ionicons
                    name={scanRunning ? 'stop-circle-outline' : scanResults.length > 0 ? 'refresh-outline' : 'play-outline'}
                    size={16}
                    color="#fff"
                  />
                  <Text style={S.primaryBtnText}>
                    {scanRunning ? 'Stop Scan' : scanResults.length > 0 ? 'Refresh' : 'Scan All Users'}
                  </Text>
                </Pressable>
                {scanProgress.done && scanResults.length > 0 && !scanLoadedFromCache && (
                  <Pressable style={[S.secondaryBtn, { borderColor: c.primary }]} onPress={() => runScan(true)}>
                    <Text style={[S.secondaryBtnText, { color: c.primary }]}>Load More</Text>
                  </Pressable>
                )}
                {scanResults.some(r => !(r._fixApplied || r.fixApplied) && !r.needsReview && (r.severity === 'low' || (r.severity === 'medium' && r.errors.some(e => e.includes('not an array'))))) && (
                  <Pressable
                    style={[S.secondaryBtn, { borderColor: '#2563eb', flexDirection: 'row', alignItems: 'center', gap: 6, opacity: bulkFixRunning ? 0.6 : 1 }]}
                    onPress={fixAllLow}
                    disabled={bulkFixRunning}
                  >
                    <Ionicons name="flash-outline" size={14} color="#2563eb" />
                    <Text style={[S.secondaryBtnText, { color: '#2563eb' }]}>
                      {bulkFixRunning ? 'Fixing…' : 'Fix structural issues'}
                    </Text>
                  </Pressable>
                )}
              </View>

              {/* Progress */}
              {(scanRunning || scanProgress.scanned > 0) && (() => {
                const isFullyFixed  = r => !!(r._fixApplied || r.fixApplied) && !r.needsReview;
                const isAwaiting    = r => !!(r._fixApplied || r.fixApplied) &&  !!r.needsReview;
                const active        = scanResults.filter(r => !isFullyFixed(r) && !isAwaiting(r));
                const awaitingItems = scanResults.filter(isAwaiting);
                const criticalUsers = groupByUser(active.filter(r => r.severity === 'critical')).length;
                const notOptUsers   = groupByUser(active.filter(r => r.severity !== 'critical')).length;
                const awaitingUsers = groupByUser(awaitingItems).length;
                const allClear = scanProgress.done && !scanRunning && active.length === 0 && awaitingItems.length === 0 && scanProgress.scanned > 0;
                return (
                  <View style={[S.progressRow, { backgroundColor: allClear ? '#f0fdf4' : c.bgSection }]}>
                    <Text style={[{ color: allClear ? '#15803d' : c.textSecondary, fontSize: 13, fontWeight: allClear ? '600' : '400' }]}>
                      {scanRunning
                        ? `Scanning… ${scanProgress.scanned} profiles checked so far`
                        : scanLoadedFromCache
                          ? `Cached — ${scanProgress.scanned} profiles checked`
                          : `Scan complete — ${scanProgress.scanned} profiles checked`}
                      {!scanRunning && allClear && ' · ✓ All clear — no issues found'}
                      {!scanRunning && !allClear && criticalUsers > 0 && (
                        ` · ${criticalUsers} user${criticalUsers !== 1 ? 's' : ''} need urgent attention`
                      )}
                      {!scanRunning && !allClear && notOptUsers > 0 && (
                        ` · ${notOptUsers} not optimized`
                      )}
                      {!scanRunning && awaitingUsers > 0 && (
                        ` · ${awaitingUsers} awaiting graduate action`
                      )}
                      {scanLoadedFromCache ? ' · click Refresh to re-scan' : ''}
                    </Text>
                  </View>
                );
              })()}

              {/* Search + Filters */}
              {scanResults.length > 0 && (
                <View style={{ marginTop: 10, gap: 8 }}>
                  <View style={S.searchRow}>
                    <TextInput
                      style={[S.searchInput, { backgroundColor: c.inputBg, color: c.textPrimary, borderColor: c.border }]}
                      placeholder="Search by email, UID, or widget ID…"
                      placeholderTextColor={c.textMuted}
                      value={scanSearchQuery}
                      onChangeText={setScanSearchQuery}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                    <FilterPill label="All" active={scanSeverityFilter === 'all'} onPress={() => setScanSeverityFilter('all')} primary={c.primary} />
                    <FilterPill label="Needs attention" active={scanSeverityFilter === 'critical'} onPress={() => setScanSeverityFilter('critical')} primary={c.primary} color={SEVERITY_COLOR.critical} />
                    <FilterPill label="Not optimized" active={scanSeverityFilter === 'medium'} onPress={() => setScanSeverityFilter('medium')} primary={c.primary} color={SEVERITY_COLOR.medium} />
                    {scanResults.some(r => !!(r._fixApplied || r.fixApplied) && !!r.needsReview) && (
                      <FilterPill label="Awaiting grad action" active={scanSeverityFilter === 'awaiting'} onPress={() => setScanSeverityFilter('awaiting')} primary={c.primary} color="#7c3aed" />
                    )}
                    <FilterPill label="Fully fixed" active={scanSeverityFilter === 'fixed'} onPress={() => setScanSeverityFilter('fixed')} primary={c.primary} color="#16a34a" />
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                    <FilterPill label="All types" active={scanTypeFilter === 'all'} onPress={() => setScanTypeFilter('all')} primary={c.primary} />
                    {[...new Set(scanResults.filter(r => {
                      const fullyFixed = !!(r._fixApplied || r.fixApplied) && !r.needsReview;
                      return !fullyFixed;
                    }).map(r => r.widgetType))].filter(Boolean).map(type => (
                      <FilterPill key={type} label={widgetTypeName(type)} active={scanTypeFilter === type} onPress={() => setScanTypeFilter(type)} primary={c.primary} />
                    ))}
                  </View>
                </View>
              )}
            </View>

            {/* Results */}
            {filteredScanResults.length === 0 && scanProgress.done && (
              <View style={{ alignItems: 'center', paddingVertical: 32 }}>
                {scanResults.length === 0 ? (
                  <Text style={[S.emptyNote, { color: c.textMuted }]}>Click "Scan All Users" to check widget health across all graduates.</Text>
                ) : scanResults.filter(r => {
                    const fullyFixed = !!(r._fixApplied || r.fixApplied) && !r.needsReview;
                    const awaiting   = !!(r._fixApplied || r.fixApplied) &&  !!r.needsReview;
                    return !fullyFixed && !awaiting;
                  }).length === 0 && !scanResults.some(r => !!(r._fixApplied || r.fixApplied) && !!r.needsReview) ? (
                  <>
                    <Text style={{ fontSize: 32, marginBottom: 8 }}>✓</Text>
                    <Text style={{ fontSize: 16, fontWeight: '700', color: '#15803d', marginBottom: 4 }}>All clear</Text>
                    <Text style={[S.emptyNote, { color: '#4b7a5a', textAlign: 'center' }]}>No widget issues found. All graduates' pages are looking good.</Text>
                  </>
                ) : (
                  <Text style={[S.emptyNote, { color: c.textMuted }]}>No widgets match the current filter. Try selecting "All" to see all issues.</Text>
                )}
              </View>
            )}

            {groupByUser(filteredScanResults).map(user => {
              const name = displayName(user);
              const ini  = initials(name);
              const isWidgetFullyFixed = w => !!(w._fixApplied || w.fixApplied) && !w.needsReview;
              const isWidgetAwaiting   = w => !!(w._fixApplied || w.fixApplied) &&  !!w.needsReview;
              const allFullyFixed = user.widgets.every(isWidgetFullyFixed);
              const anyAwaiting   = user.widgets.some(isWidgetAwaiting);
              const worstSev = user.widgets.some(w => w.severity === 'critical' && !isWidgetAwaiting(w)) ? 'critical'
                : user.widgets.some(w => w.severity === 'medium'   && !isWidgetAwaiting(w)) ? 'medium' : 'low';
              const sevD = allFullyFixed ? severityDisplay('ok', true, false)
                : anyAwaiting && user.widgets.every(w => isWidgetFullyFixed(w) || isWidgetAwaiting(w))
                  ? severityDisplay(null, false, true)
                  : severityDisplay(worstSev, false, false);
              const uid = user.uid || user.docId;
              const pageUrl = eventsPageUrl(user.pagesEvents, user.uic);
              return (
                <View key={user.key} style={[S.userWidgetCard, { borderLeftColor: sevD.color }]}>
                  {/* User header */}
                  <View style={S.userWidgetHeader}>
                    {/* Avatar */}
                    {user.profilePictureURL ? (
                      <img src={user.profilePictureURL} style={{ width: 44, height: 44, borderRadius: 22, objectFit: 'cover', flexShrink: 0 }} alt="" />
                    ) : (
                      <View style={[S.avatarCircle, { backgroundColor: sevD.color + '22' }]}>
                        <Text style={[S.avatarInitials, { color: sevD.color }]}>{ini}</Text>
                      </View>
                    )}
                    {/* Name / email */}
                    <View style={{ flex: 1, marginLeft: 12 }}>
                      <Text style={S.userWidgetName}>{name}</Text>
                      <Text style={S.userWidgetEmail}>{user.email}</Text>
                    </View>
                    {/* Status badge + count */}
                    <View style={{ alignItems: 'flex-end', gap: 4 }}>
                      <View style={[S.sevPill, { backgroundColor: sevD.bg }]}>
                        <Text style={[S.sevPillText, { color: sevD.color }]}>{sevD.label}</Text>
                      </View>
                      <Text style={S.widgetCountText}>
                        {user.widgets.length} issue{user.widgets.length !== 1 ? 's' : ''}
                      </Text>
                    </View>
                  </View>

                  {/* Widget rows */}
                  <View style={{ marginTop: 10, gap: 6 }}>
                    {user.widgets.map((result, wi) => {
                      const wIsFullyFixed = !!(result._fixApplied || result.fixApplied) && !result.needsReview;
                      const wIsAwaiting   = !!(result._fixApplied || result.fixApplied) &&  !!result.needsReview;
                      const wSevD = severityDisplay(result.severity, wIsFullyFixed, wIsAwaiting);
                      const isActing = actionLoading === result.widgetId;
                      return (
                        <View key={`${result.widgetId}_${wi}`} style={[S.widgetInnerRow, { backgroundColor: wSevD.bg + '55' }]}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <View style={[S.sevDot, { backgroundColor: wSevD.color }]} />
                            <Text style={S.widgetInnerType}>{widgetTypeName(result.widgetType)}</Text>
                            {result.label && result.label !== '(no title)' && result.label !== '(empty)' && (
                              <Text style={S.widgetInnerLabel}>"{result.label}"</Text>
                            )}
                            <View style={{ flex: 1 }} />
                            <Text style={[S.sevPillText, { color: wSevD.color }]}>{wSevD.label}</Text>
                          </View>
                          {wIsAwaiting ? (
                            <Text style={[S.widgetInnerHint, { color: '#7c3aed' }]}>
                              Widget was archived by admin — graduate has been notified via their page banner and needs to recreate it.
                            </Text>
                          ) : (
                            result.errors.map((e, i) => (
                              <Text key={i} style={[S.widgetInnerError, { color: wIsFullyFixed ? '#6b7280' : '#374151' }]}>
                                {wIsFullyFixed ? '✓ ' : '→ '}{plainError(e)}
                              </Text>
                            ))
                          )}
                          {!wIsAwaiting && result.severity === 'low' && (
                            <Text style={S.widgetInnerHint}>
                              This is a minor structural issue — it will correct itself automatically when the graduate next saves their page.
                            </Text>
                          )}
                        </View>
                      );
                    })}
                  </View>

                  {/* Action buttons */}
                  <View style={S.userWidgetActions}>
                    <Pressable
                      style={S.viewPageBtn}
                      onPress={() => pageUrl && window.open(pageUrl, '_blank')}
                    >
                      <Ionicons name="eye-outline" size={14} color="#fff" style={{ marginRight: 5 }} />
                      <Text style={S.viewPageBtnText}>View Their Events Page</Text>
                    </Pressable>
                    {user.widgets.some(w => w.severity === 'critical' && !(w._fixApplied || w.fixApplied) && !w.needsReview) && (
                      <Pressable
                        style={[S.fixBtn, { opacity: actionLoading ? 0.6 : 1 }]}
                        disabled={!!actionLoading}
                        onPress={() => {
                          const first = user.widgets.find(w => w.severity === 'critical' && !(w._fixApplied || w.fixApplied) && !w.needsReview);
                          if (first) fixScanResult(first);
                        }}
                      >
                        <Ionicons name="construct-outline" size={14} color="#1e40af" style={{ marginRight: 5 }} />
                        <Text style={S.fixBtnText}>Repair Widget</Text>
                      </Pressable>
                    )}
                    <Pressable
                      style={S.explorerBtn}
                      onPress={() => {
                        pendingExplorerSearchRef.current = true;
                        setExplorerQuery(user.email);
                        setSubTab('explorer');
                      }}
                    >
                      <Ionicons name="search-outline" size={14} color="#5b21b6" style={{ marginRight: 5 }} />
                      <Text style={S.explorerBtnText}>Deep Inspect</Text>
                    </Pressable>
                  </View>
                </View>
              );
            })}
          </View>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            AUDIT TIMELINE
        ══════════════════════════════════════════════════════════════════════ */}
        {subTab === 'audit' && (
          <View>
            {/* Filter row — search inputs */}
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
              <TextInput
                style={[S.searchInput, { flex: 1, minWidth: 140, maxWidth: 240 }]}
                placeholder="Filter by UID…"
                placeholderTextColor={c.textMuted}
                value={auditUidFilter}
                onChangeText={setAuditUidFilter}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TextInput
                style={[S.searchInput, { flex: 1, minWidth: 140, maxWidth: 240 }]}
                placeholder="Filter by widget ID…"
                placeholderTextColor={c.textMuted}
                value={auditWidgetIdFilter}
                onChangeText={setAuditWidgetIdFilter}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <Pressable style={[S.primaryBtn, { backgroundColor: c.primary }]} onPress={() => { setAuditCursor(null); loadAuditTimeline(false); }}>
                <Text style={S.primaryBtnText}>Refresh</Text>
              </Pressable>
            </View>
            {/* Source filter pills */}
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
              <Text style={{ color: c.textMuted, fontSize: 12 }}>Source:</Text>
              {[['all', 'All sources', undefined], ['user', 'user', '#16a34a'], ['admin', 'admin', '#b45309'], ['system', 'system', '#2563eb']].map(([val, lbl, col]) => (
                <FilterPill key={val} label={lbl} active={auditSourceFilter === val}
                  onPress={() => setAuditSourceFilter(val)} primary={c.primary} color={col} />
              ))}
            </View>
            {/* Action type filter pills */}
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
              <Text style={{ color: c.textMuted, fontSize: 12 }}>Action:</Text>
              {['all', 'widget_created', 'widget_updated', 'widget_fixed', 'widget_deleted', 'widget_validation_failed'].map(f => (
                <FilterPill key={f} label={f === 'all' ? 'All actions' : f} active={auditFilter === f}
                  onPress={() => { setAuditFilter(f); setAuditCursor(null); }} primary={c.primary} />
              ))}
            </View>

            {auditLoading && <ActivityIndicator color={c.primary} style={{ marginVertical: 20 }} />}

            {!auditLoading && auditTimeline.length === 0 && (
              <Text style={[S.emptyNote, { color: c.textMuted }]}>
                No widget logs loaded yet. Click Refresh to load recent activity.
              </Text>
            )}

            {(() => {
              const uidNeedle      = auditUidFilter.trim().toLowerCase();
              const widgetNeedle   = auditWidgetIdFilter.trim().toLowerCase();
              const visible = auditTimeline.filter(log => {
                // UID / path filter
                if (uidNeedle && !(
                  (log.uid      && log.uid.toLowerCase().includes(uidNeedle)) ||
                  (log._path    && log._path.toLowerCase().includes(uidNeedle)) ||
                  (log.adminId  && log.adminId.toLowerCase().includes(uidNeedle))
                )) return false;
                // Widget ID filter
                if (widgetNeedle && !(log.widgetId && log.widgetId.toLowerCase().includes(widgetNeedle))) return false;
                // Source filter — normalise legacy 'UI' records to 'user'
                if (auditSourceFilter !== 'all') {
                  const src = log.source === 'UI' ? 'user' : (log.source || 'system');
                  if (src !== auditSourceFilter) return false;
                }
                return true;
              });
              const isFiltered = uidNeedle || widgetNeedle || auditSourceFilter !== 'all';
              return (
                <>
                  {isFiltered && (
                    <Text style={[S.emptyNote, { color: c.textMuted, marginBottom: 8 }]}>
                      Showing {visible.length} of {auditTimeline.length} records
                      {uidNeedle     ? ` · uid: "${auditUidFilter}"` : ''}
                      {widgetNeedle  ? ` · widget: "${auditWidgetIdFilter}"` : ''}
                      {auditSourceFilter !== 'all' ? ` · source: ${auditSourceFilter}` : ''}
                    </Text>
                  )}
                  {visible.map(log => (
                    <AuditLogRow key={log.id} log={log} textPrimary={c.textPrimary} textMuted={c.textMuted}
                      danger={c.danger} border={c.border} onDiff={setDiffModal} showPath />
                  ))}
                </>
              );
            })()}

            {auditTimeline.length > 0 && !auditLoading && (
              <Pressable style={[S.secondaryBtn, { borderColor: c.primary, marginTop: 12 }]} onPress={() => loadAuditTimeline(true)}>
                <Text style={[S.secondaryBtnText, { color: c.primary }]}>Load More</Text>
              </Pressable>
            )}
          </View>
        )}

      </ScrollView>

      {/* ── Diff Modal ── */}
      {diffModal && (
        <Modal transparent animationType="fade" onRequestClose={() => setDiffModal(null)}>
          <View style={S.modalOverlay}>
            <View style={[S.modalBox, { backgroundColor: c.bg, borderColor: c.border }]}>
              <Text style={[S.modalTitle, { color: c.textPrimary }]}>{diffModal.label}</Text>
              <ScrollView style={{ maxHeight: 420 }}>
                {diffModal.changes?.length > 0 && (
                  <View style={{ marginBottom: 12 }}>
                    <Text style={[S.diffSectionLabel, { color: c.textSecondary }]}>Changes applied:</Text>
                    {diffModal.changes.map((ch, i) => (
                      <Text key={i} style={[S.diffChangeLine, { color: c.textPrimary }]}>• {ch}</Text>
                    ))}
                  </View>
                )}
                {computeDiff(diffModal.before, diffModal.after).map(({ key, before, after }) => (
                  <View key={key} style={[S.diffRow, { borderColor: c.border }]}>
                    <Text style={[S.diffKey, { color: c.textSecondary }]}>{key}</Text>
                    <Text style={[S.diffBefore, { color: c.danger }]}>− {before}</Text>
                    <Text style={[S.diffAfter, { color: c.success }]}>+ {after}</Text>
                  </View>
                ))}
                {computeDiff(diffModal.before, diffModal.after).length === 0 && (
                  <Text style={{ color: c.textMuted, fontSize: 13 }}>No field differences to display.</Text>
                )}
              </ScrollView>
              <Pressable style={[S.primaryBtn, { backgroundColor: c.primary, marginTop: 16 }]} onPress={() => setDiffModal(null)}>
                <Text style={S.primaryBtnText}>Close</Text>
              </Pressable>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────────

function SeverityBadge({ label, bg, fg }) {
  return (
    <View style={[S.badge, { backgroundColor: bg }]}>
      <Text style={[S.badgeText, { color: fg }]}>{label}</Text>
    </View>
  );
}

function ActionBtn({ iconName, label, bg, fg, loading, onPress }) {
  return (
    <Pressable style={[S.actionBtn, { backgroundColor: bg, opacity: loading ? 0.5 : 1 }]} onPress={onPress} disabled={loading}>
      <View style={S.actionBtnContent}>
        {iconName && <Ionicons name={iconName} size={14} color={fg} style={{ marginRight: 6 }} />}
        <Text style={[S.actionBtnText, { color: fg }]}>{loading ? '…' : label}</Text>
      </View>
    </Pressable>
  );
}

function FilterPill({ label, active, onPress, primary, color }) {
  return (
    <Pressable
      style={[S.filterPill, active && { backgroundColor: primary, borderColor: primary }]}
      onPress={onPress}
    >
      <Text style={[S.filterPillText, { color: active ? '#fff' : (color || '#4b5563') }]}>{label}</Text>
    </Pressable>
  );
}

const SOURCE_COLOR = { user: '#16a34a', admin: '#b45309', system: '#2563eb', UI: '#16a34a' };
const SOURCE_BG    = { user: '#dcfce7', admin: '#fef3c7', system: '#dbeafe', UI: '#dcfce7' };

function AuditLogRow({ log, textPrimary, textMuted, danger, border, onDiff, showPath }) {
  const hasDiff = !!(log.beforeSnapshot || log.afterSnapshot);
  const changeTypeColor = {
    widget_created: '#22c55e', widget_updated: '#3b82f6', widget_repaired: '#f59e0b',
    widget_deleted: '#ef4444', widget_validation_failed: '#dc2626', widget_migrated: '#8b5cf6',
  };
  // Normalise legacy 'UI' source label to 'user' for display
  const src = log.source === 'UI' ? 'user' : (log.source || 'system');

  return (
    <View style={[S.auditRow, { borderBottomColor: border }]}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text style={[S.auditChangeType, { color: changeTypeColor[log.changeType] || textPrimary }]}>
            {log.changeType}
          </Text>
          <View style={[S.sourceBadge, { backgroundColor: SOURCE_BG[src] || '#f3f4f6' }]}>
            <Text style={[S.sourceBadgeText, { color: SOURCE_COLOR[src] || '#374151' }]}>{src}</Text>
          </View>
        </View>
        {hasDiff && (
          <Pressable onPress={() => onDiff({ label: log.changeType, before: log.beforeSnapshot, after: log.afterSnapshot, changes: [] })}>
            <Text style={{ color: '#6b21a8', fontSize: 12, fontWeight: '600' }}>View Diff ↗</Text>
          </Pressable>
        )}
      </View>
      <Text style={[S.auditMeta, { color: textMuted }]}>
        {fmtTs(log.timestamp)}
        {log.widgetType ? ` · ${log.widgetType}` : ''}
        {log.widgetId   ? ` · widget: ${log.widgetId}` : ''}
        {log.uid        ? ` · uid: ${log.uid}` : ''}
        {log.adminId    ? ` · admin: ${log.adminId}` : ''}
        {log.caller     ? ` · via: ${log.caller}` : ''}
      </Text>
      {showPath && log._path && (
        <Text style={[S.auditMeta, { color: textMuted }]}>path: {log._path}</Text>
      )}
      {log.errors?.length > 0 && (
        <Text style={{ color: danger, fontSize: 11, marginTop: 2 }}>Errors: {log.errors.join(', ')}</Text>
      )}
      {log.beforeSnapshot && (
        <Text style={{ color: textMuted, fontSize: 11, fontStyle: 'italic' }}>
          Before: title="{log.beforeSnapshot.eventTitle || ''}" date="{log.beforeSnapshot.eventDate || ''}"
        </Text>
      )}
      {log.afterSnapshot && (
        <Text style={{ color: textMuted, fontSize: 11, fontStyle: 'italic' }}>
          After: title="{log.afterSnapshot.eventTitle || ''}" date="{log.afterSnapshot.eventDate || ''}"
        </Text>
      )}
    </View>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  pageHeader:   { paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1 },
  pageTitle:    { fontSize: 20, fontWeight: '700', marginBottom: 2 },
  pageSubtitle: { fontSize: 13 },

  tabBar:    { flexDirection: 'row', borderBottomWidth: 1, paddingHorizontal: 16 },
  tabBtn:    { paddingHorizontal: 16, paddingVertical: 12, marginRight: 4 },
  tabBtnContent: { flexDirection: 'row', alignItems: 'center' },
  tabBtnText:{ fontSize: 13, fontWeight: '600' },

  statusBanner: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 },

  searchRow:  { flexDirection: 'row', gap: 8, marginBottom: 16, alignItems: 'center' },
  searchInput:{ flex: 1, height: 40, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, fontSize: 14 },

  primaryBtn:    { paddingHorizontal: 18, paddingVertical: 10, borderRadius: 8 },
  primaryBtnText:{ color: '#fff', fontWeight: '600', fontSize: 14 },
  secondaryBtn:  { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, borderWidth: 1 },
  secondaryBtnText: { fontSize: 14, fontWeight: '600' },
  warningBtn:    { padding: 10, borderRadius: 8, alignItems: 'center', backgroundColor: '#f59e0b', marginTop: 12 },
  warningBtnText:{ color: '#fff', fontWeight: '700', fontSize: 14 },

  userCard:   { borderWidth: 1, borderRadius: 12, padding: 16, marginBottom: 12 },
  archiveBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1, borderColor: '#93c5fd', backgroundColor: '#eff6ff' },
  archiveBtnText: { color: '#1e40af', fontWeight: '600', fontSize: 12, marginLeft: 4 },
  userName:   { fontSize: 16, fontWeight: '600' },
  userMeta:   { fontSize: 12, marginTop: 2 },

  badge:      { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 },
  badgeText:  { fontSize: 12, fontWeight: '600' },

  widgetRow:  { borderWidth: 2, borderRadius: 10, padding: 12, marginBottom: 10 },
  widgetRowHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  widgetTitle:{ fontSize: 14, fontWeight: '600' },
  widgetMeta: { fontSize: 11, marginTop: 2 },
  sevBadge:   { fontSize: 12, fontWeight: '700', marginLeft: 8, flexShrink: 0 },
  errorLine:  { fontSize: 12 },

  rawPanel:   { marginTop: 10, padding: 10, borderRadius: 8, borderWidth: 1 },
  rawLabel:   { fontSize: 11, marginBottom: 4 },
  rawText:    { fontSize: 11, fontFamily: 'monospace' },

  actionRow:  { flexDirection: 'row', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  actionBtn:  { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  actionBtnContent: { flexDirection: 'row', alignItems: 'center' },
  actionBtnText: { fontSize: 12, fontWeight: '600' },

  filterPill: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, borderWidth: 1, borderColor: '#d1d5db' },
  filterPillText: { fontSize: 12, fontWeight: '500' },

  logOnlyBadge:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, backgroundColor: '#fef3c7', borderWidth: 1, borderColor: '#fcd34d' },
  logOnlyText:     { fontSize: 12, fontWeight: '600', color: '#92400e' },
  inspectOnlyBadge:{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, backgroundColor: '#fff7ed', borderWidth: 1, borderColor: '#fed7aa' },
  inspectOnlyText: { fontSize: 12, fontWeight: '600', color: '#9a3412' },
  alreadyFixedBadge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, backgroundColor: '#dcfce7', borderWidth: 1, borderColor: '#86efac' },
  alreadyFixedText:  { fontSize: 12, fontWeight: '600', color: '#166534' },
  ownerNotifiedBadge:{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: '#f0fdf4', borderWidth: 1, borderColor: '#86efac' },
  ownerNotifiedText: { fontSize: 11, fontWeight: '600', color: '#166534' },

  scanHeader: { borderWidth: 1, borderRadius: 10, padding: 14, marginBottom: 12 },
  progressRow:{ padding: 10, borderRadius: 8, marginBottom: 4 },

  auditSection: { marginTop: 10, borderTopWidth: 1, paddingTop: 10 },
  auditTitle: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  auditRow:   { borderBottomWidth: 1, paddingVertical: 8 },
  auditChangeType: { fontSize: 13, fontWeight: '700' },
  auditMeta:  { fontSize: 11, marginTop: 2 },

  emptyNote:  { fontSize: 13, fontStyle: 'italic' },

  sourceBadge:     { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 10 },
  sourceBadgeText: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4 },

  // ── Grouped user widget cards (Corrupted Widgets tab) ──────────────────────
  userWidgetCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderLeftWidth: 4,
    padding: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 4,
  },
  userWidgetHeader: { flexDirection: 'row', alignItems: 'center' },
  avatarCircle: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  avatarInitials: { fontSize: 16, fontWeight: '700' },
  userWidgetName: { fontSize: 15, fontWeight: '700', color: '#111827' },
  userWidgetEmail: { fontSize: 12, color: '#6b7280', marginTop: 1 },
  sevPill: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 20 },
  sevPillText: { fontSize: 12, fontWeight: '700' },
  widgetCountText: { fontSize: 11, color: '#9ca3af' },

  widgetInnerRow: { borderRadius: 8, padding: 10 },
  widgetInnerType: { fontSize: 13, fontWeight: '700', color: '#1f2937' },
  widgetInnerLabel: { fontSize: 12, color: '#6b7280', fontStyle: 'italic', flex: 1, flexShrink: 1 },
  widgetInnerError: { fontSize: 13, marginTop: 2, marginLeft: 16, lineHeight: 18 },
  widgetInnerHint: { fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginTop: 4, marginLeft: 16 },
  sevDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },

  userWidgetActions: { flexDirection: 'row', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  viewPageBtn:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: '#4c956c' },
  viewPageBtnText: { color: '#fff', fontSize: 13, fontWeight: '600' },
  fixBtn:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: '#dbeafe', borderWidth: 1, borderColor: '#93c5fd' },
  fixBtnText:   { color: '#1e40af', fontSize: 13, fontWeight: '600' },
  explorerBtn:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: '#ede9fe', borderWidth: 1, borderColor: '#c4b5fd' },
  explorerBtnText: { color: '#5b21b6', fontSize: 13, fontWeight: '600' },

  // Diff modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox:     { width: '100%', maxWidth: 640, borderRadius: 12, padding: 20, borderWidth: 1 },
  modalTitle:   { fontSize: 16, fontWeight: '700', marginBottom: 14 },
  diffSectionLabel: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  diffChangeLine:   { fontSize: 13, marginBottom: 2 },
  diffRow:    { borderBottomWidth: 1, paddingVertical: 6 },
  diffKey:    { fontSize: 12, fontWeight: '600', marginBottom: 2 },
  diffBefore: { fontSize: 12, fontFamily: 'monospace' },
  diffAfter:  { fontSize: 12, fontFamily: 'monospace' },
});
